import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useCallback } from "react";
import { useMutation } from "urql";

import { Button } from "@/components/ui/button";
import { ArticleStatus } from "@/gen/graphql";
import { setStatusArticleMutation } from "@/routes/articles/queries";

const STATUS_OPTIONS: { label: string; value: ArticleStatus }[] = [
    { label: "Unlisted", value: "UNLISTED" },
    { label: "Hidden", value: "HIDDEN" },
    { label: "Draft", value: "DRAFT" },
    { label: "Published", value: "PUBLISHED" },
];

export function ToggleArticleStatusButton({
    articleId,
    status,
    draftUnavailable,
    onStatusChanged,
}: {
    articleId?: string;
    status?: ArticleStatus;
    draftUnavailable?: boolean;
    onStatusChanged: (status: ArticleStatus) => void;
}) {
    const [_, setStatusArticle] = useMutation(setStatusArticleMutation);

    const statusOptions = STATUS_OPTIONS.filter(opt => !draftUnavailable || opt.value !== "DRAFT");

    const onStatusChange = useCallback(
        (newStatus: ArticleStatus) => {
            if (articleId == null) return;

            setStatusArticle({
                id: articleId,
                status: newStatus,
            }).then(() => {
                onStatusChanged(newStatus);
            });
        },
        [articleId, onStatusChanged, setStatusArticle]
    );

    return (
        <div className="flex">
            <Button
                className="rounded-r-none"
                variant="secondary"
                onClick={() => {
                    if (articleId != null) {
                        onStatusChange(status === "PUBLISHED" ? "HIDDEN" : "PUBLISHED");
                    }
                }}
            >
                {status === "PUBLISHED" ? "Unpublish" : `Publish (${status})`}
            </Button>

            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <Button
                        variant="secondary"
                        className="rounded-l-none border-l-[1px] border-l-white/20 px-2"
                    >
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                </DropdownMenu.Trigger>

                <DropdownMenu.Portal>
                    <DropdownMenu.Content className="min-w-[160px] rounded-md bg-white p-1 shadow-lg">
                        {statusOptions
                            .filter(opt => opt.value !== status)
                            .map(option => (
                                <DropdownMenu.Item
                                    key={option.value}
                                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-slate-100 data-[disabled]:pointer-events-none data-[highlighted]:bg-slate-100"
                                    onClick={() => {
                                        onStatusChange(option.value as ArticleStatus);
                                    }}
                                >
                                    {option.label}
                                </DropdownMenu.Item>
                            ))}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </div>
    );
}
